import type {
  BlockId,
  ColorId,
} from '../../minigame/match2-timestep/game/defs/block';
import { colorToBlockId } from '../../minigame/match2-timestep/game/defs/block';
import type { BlockEntity } from '../../minigame/match2-timestep/game/entities/BlockEntity';
import {
  despawnBlockEntity,
  spawnBlockEntity,
} from '../../minigame/match2-timestep/game/entities/BlockEntity';
import type { GameScene } from '../../minigame/match2-timestep/game/GameScene';
import { blockIterateAll } from '../../minigame/match2-timestep/game/util/blockTools';
import { arrayShuffle } from '../../minigame/match2-timestep/lib/util/jsTools';
import { waitAFrame } from '../../minigame/match2-timestep/lib/util/waitAFrame';
import {
  type PrismPainterPaintColor,
  runPrismPainterPowerAnimation,
} from './dreamDrift.power.painter.anim';

/** Board colors considered by Prism Painter in deterministic tie priority. */
const PRISM_PAINTER_COLORS = [
  'blue',
  'green',
  'purple',
  'red',
  'yellow',
] as const satisfies readonly PrismPainterPaintColor[];

/** Concrete basic-tile color supported by Prism Painter. */
type PrismPainterColor = (typeof PRISM_PAINTER_COLORS)[number];

/** Tuning values for the Prism Painter opening power. */
type PrismPainterPowerConfig = {
  /** Maximum number of tiles converted per level. */
  conversionCap: number;
  /** Target number of conversions when enough non-target tiles are available. */
  minimumConversions: number;
  /** Minimum number of eligible colors required before conversion is allowed. */
  minimumDistinctColors: number;
};

/** Current Prism Painter tuning. */
const PRISM_PAINTER_POWER_CONFIG: PrismPainterPowerConfig = {
  conversionCap: 6,
  minimumConversions: 4,
  minimumDistinctColors: 2,
};

/** Eligible board entities sharing one Prism Painter color. */
type PrismPainterColorGroup = {
  /** Basic-tile color represented by the group. */
  color: PrismPainterColor;
  /** Eligible basic entities currently showing the color. */
  targets: BlockEntity[];
};

/** Selected Prism Painter targets and the basic block id replacing them. */
type PrismPainterConversionPlan = {
  /** Most-common color applied to every selected replacement. */
  targetColor: PrismPainterColor;
  /** Ordinary basic block id used for every selected replacement. */
  targetBlockId: BlockId;
  /** Ordered eligible basic entities repainted by the presentation. */
  targets: BlockEntity[];
};

/**
 * Repaint least-common eligible colors into the most common color.
 *
 * @param game Active Dream Drift Match-2 runtime.
 * @returns Promise resolved after every selected tile is replaced.
 */
export async function runPrismPainterPower(game: GameScene): Promise<void> {
  // Snapshot the complete logical conversion plan before the board begins changing.
  const conversionPlan = createPrismPainterConversionPlan(game);
  const spawnPositions =
    conversionPlan?.targets.map((target) => target.c.position.mapPosition) ??
    [];
  const targetPositions = spawnPositions.map((position) => ({
    x: position.x,
    y: position.y,
  }));
  const appliedTargets = new Set<BlockEntity>();

  /** Apply one selected conversion when the brush reaches its presentation beat. */
  const applyPaintStep = (targetIndex: number): void => {
    // Ignore callbacks when the settled board had no meaningful conversion plan.
    if (!conversionPlan) return;

    // Ignore invalid indexes and repeated callbacks for an already changed target.
    const target = conversionPlan.targets[targetIndex];
    const position = spawnPositions[targetIndex];
    if (!target || !position || appliedTargets.has(target)) return;

    // Replace the eligible basic tile without recording color-goal progress.
    appliedTargets.add(target);
    despawnBlockEntity(game, target, true);
    spawnBlockEntity(game, { id: conversionPlan.targetBlockId }, position);
  };

  // Let the authored brush synchronize each logical color conversion.
  await runPrismPainterPowerAnimation(
    game,
    targetPositions,
    conversionPlan?.targetColor,
    applyPaintStep,
  );

  // Target-free presentations have no board activity to settle.
  if (appliedTargets.size === 0) return;

  // Give the replacement system time to register any asynchronous activations.
  await waitAFrame();

  // Keep puzzle input blocked until all resulting board activations complete.
  await game.sessionEntity.c.phase.waitActivationsComplete();

  // Finish immediately when replacements leave the board in its input phase.
  const boardNeedsSettling = game.phaseSystem.phase !== 'input';
  if (!boardNeedsSettling) return;

  // Wait for the normal input phase after every resulting reaction settles.
  await game.phaseSystem.waitForPhase('input');
}

/**
 * Select the eligible basic tiles converted by Prism Painter.
 *
 * @param game Active Dream Drift Match-2 runtime.
 * @returns Complete conversion plan, or undefined without meaningful targets.
 */
function createPrismPainterConversionPlan(
  game: GameScene,
): PrismPainterConversionPlan | undefined {
  // Count only eligible basic tiles that the power may safely replace.
  const colorGroups = collectPrismPainterColorGroups(game);

  // A distinct source and target color are required for a meaningful conversion.
  if (colorGroups.length < PRISM_PAINTER_POWER_CONFIG.minimumDistinctColors) {
    return undefined;
  }

  // Resolve the most common target color using the fixed tie priority.
  const targetGroup = selectMostCommonPrismPainterColorGroup(colorGroups);

  // Order every non-target color from least common to most common.
  const sourceGroups = selectPrismPainterSourceGroups(
    colorGroups,
    targetGroup.color,
  );
  if (!sourceGroups.length) return undefined;

  // Normalize the maximum and minimum conversion tuning values.
  const conversionCap = Math.max(
    0,
    Math.floor(PRISM_PAINTER_POWER_CONFIG.conversionCap),
  );
  if (conversionCap === 0) return undefined;
  const minimumConversions = Math.min(
    conversionCap,
    Math.max(0, Math.floor(PRISM_PAINTER_POWER_CONFIG.minimumConversions)),
  );

  // Convert the full scarcest group, then draw from later groups to the minimum.
  const conversionTargets: BlockEntity[] = [];
  for (const [sourceIndex, sourceGroup] of sourceGroups.entries()) {
    const minimumReached = conversionTargets.length >= minimumConversions;
    if (sourceIndex > 0 && minimumReached) break;

    const remainingCapacity = conversionCap - conversionTargets.length;
    if (remainingCapacity === 0) break;

    // Randomize which tiles are repainted when only part of a group is needed.
    const groupTargets = [...sourceGroup.targets];
    arrayShuffle(groupTargets);

    // Later groups contribute only enough tiles to reach the target minimum.
    const remainingMinimum = Math.max(
      0,
      minimumConversions - conversionTargets.length,
    );
    const groupConversionLimit =
      sourceIndex === 0
        ? remainingCapacity
        : Math.min(remainingCapacity, remainingMinimum);
    conversionTargets.push(...groupTargets.slice(0, groupConversionLimit));
  }
  if (!conversionTargets.length) return undefined;

  // Resolve the ordinary basic block id for the selected target color.
  const targetBlockId = colorToBlockId[targetGroup.color].basic;
  if (!targetBlockId) return undefined;

  // Return the complete immutable selection used throughout the presentation.
  return {
    targetColor: targetGroup.color,
    targetBlockId,
    targets: conversionTargets,
  };
}

/**
 * Collect eligible basic entities into fixed-priority color groups.
 *
 * @param game Active Dream Drift Match-2 runtime.
 * @returns Non-empty eligible color groups in deterministic priority order.
 */
function collectPrismPainterColorGroups(
  game: GameScene,
): PrismPainterColorGroup[] {
  // Initialize every supported color so result order remains deterministic.
  const targetsByColor = new Map<PrismPainterColor, BlockEntity[]>(
    PRISM_PAINTER_COLORS.map((color) => [color, []]),
  );

  // Inspect every base entity together with anything covering its board cell.
  blockIterateAll(game, (base, overlay) => {
    // Regular bubbles allow recoloring; other overlays retain their protection.
    if (overlay && overlay.c.block.blockId !== 'cage') return;

    // Obstacles and player-created powers are not eligible color tiles.
    const block = base.c.block;
    if (block.props.type !== 'basic') return;

    // Ignore unsupported colors such as the wildcard color.
    const targets = targetsByColor.get(block.props.color as PrismPainterColor);
    if (!targets) return;

    // Add the eligible basic entity to its current color group.
    targets.push(base);
  });

  // Return only colors represented by at least one eligible tile.
  return PRISM_PAINTER_COLORS.flatMap((color) => {
    const targets = targetsByColor.get(color) ?? [];
    return targets.length ? [{ color, targets }] : [];
  });
}

/**
 * Select the most common eligible board color.
 *
 * @param colorGroups Eligible groups in deterministic priority order.
 * @returns Group with the highest tile count, preferring earlier tied colors.
 */
function selectMostCommonPrismPainterColorGroup(
  colorGroups: PrismPainterColorGroup[],
): PrismPainterColorGroup {
  // Seed selection from the guaranteed first eligible color.
  let selectedGroup = colorGroups[0];

  // Replace the selection only when a strictly larger group is found.
  for (const colorGroup of colorGroups.slice(1)) {
    if (colorGroup.targets.length > selectedGroup.targets.length) {
      selectedGroup = colorGroup;
    }
  }

  // Return the most common group after all eligible colors are compared.
  return selectedGroup;
}

/**
 * Order eligible source colors from least common to most common.
 *
 * @param colorGroups Eligible groups in deterministic priority order.
 * @param targetColor Most common color receiving converted tiles.
 * @returns Non-target groups ordered by count and fixed color priority.
 */
function selectPrismPainterSourceGroups(
  colorGroups: PrismPainterColorGroup[],
  targetColor: PrismPainterColor,
): PrismPainterColorGroup[] {
  // Remove the target color before ordering the available conversion sources.
  const sourceGroups = colorGroups.filter(
    (colorGroup) => colorGroup.color !== targetColor,
  );

  // Sort by count while preserving the existing fixed priority for ties.
  sourceGroups.sort(
    (first, second) => first.targets.length - second.targets.length,
  );

  // Return every source group in the order Prism Painter should consume it.
  return sourceGroups;
}
